import { Directive, EventEmitter, inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, shareReplay, startWith, Subscription } from 'rxjs';

import { FormValueService } from 'src/app/common/guards/save-data-guard/form-value.service';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import ProjectLinks from 'src/app/common/project.links';
import { FormConstructorComponent } from 'src/app/forms/form-constructors/form-constructor/form-constructor.component';
import { FormStateService } from 'src/app/forms/form-state.service';
import { FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { unsubscribeAll } from '../../../common/app.helpers';

@Directive()
export abstract class SocialInitiativeChange<TFormModel extends object> implements OnDestroy, IDeactivate {
  @Input({ required: true }) public formContent: FormConstructorContent;

  private _formValueChanges$: ReplaySubject<TFormModel> = new ReplaySubject();

  @Output() public dirtyForm: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public formValueCanged: Observable<TFormModel> = this._formValueChanges$;

  @ViewChild(FormConstructorComponent, { static: false }) protected componentForm: FormConstructorComponent;

  public isFormChanged = false;
  public ProjectLinks: typeof ProjectLinks = ProjectLinks;

  protected router = inject(Router);
  protected formValueService = inject(FormValueService);
  private formStateService = inject(FormStateService);

  protected subscriptions$: Subscription[] = [];

  public readonly formValueChanges$: Observable<TFormModel> = this._formValueChanges$.pipe(shareReplay(1));

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public submitForm(event: Event): void {
    this.formStateService.stopAllAction(event);

    if (this.componentForm.childForm.isFormValid()) {
      this.formValueService.saveFormValue(this.componentForm.childForm.form.value);
      this.componentForm.prepareSubmitData();
      this.isFormChanged = false;
      this.changeEntity(this.componentForm.formContent.data);
    }
  }

  public abstract onCancel(): void;

  protected abstract changeEntity(data: object): void;

  protected subscribeToControlValueChanges(): void {
    if (!this.componentForm?.childForm) {
      return;
    }

    const currentForm = structuredClone(this.componentForm?.childForm?.form.value as TFormModel);

    this.subscriptions$.push(
      this.componentForm.childForm.form.valueChanges
        .pipe(startWith(this.componentForm.childForm.form.value))
        .subscribe((value: TFormModel) => {
          const rawFormValue = this.componentForm.childForm.form.getRawValue() as TFormModel;

          this._formValueChanges$.next(rawFormValue);

          const isFormChanged: boolean = this.formValueService.formChangeDetection(value, currentForm);
          this.dirtyForm.emit(isFormChanged);
          this.isFormChanged = isFormChanged;
        }),
    );
  }
}
